import { couleurDeBranche } from '@nextilt/commun';
import type { Bulle, BulleDeFeuille, Feuille, Maillon } from '../partage/api/types';
import { teinteOuAccent } from '../apparence';
import { useCadreDuHaut } from '../partage/cadreDuHaut';
import { gestesDuToucher } from '../partage/deuxGestes';
import {
  ATTEND_LE_MONDE_EN_MENTION,
  faitesSurTotal,
  NOTE_DE_LA_BRANCHE,
  unDetailDans,
} from '../partage/vocabulaire';
import { IconeDossier } from './ArbreDuProjet';
import { attendLeMonde, ceQueDitLeBout, ligneGriseDuDetail, montreesDansLArbre } from './arbreAuPouce';
import { rangerDansMonOrdre } from './rangerLaListe';
import { titreDuRond } from './rondQuiCoche';
import './DetailDUneBranche.css';

/**
 * Le détail d'une branche, au téléphone (chantier telephone-projet, 03, maquette
 * `app-au-telephone.html`, `projet()`, la branche `else if (br)`, `.fil`, `.inter`, `.paquet`,
 * `.ligne`, `.case`, `.chev`, `.ligne.ajout`, `.postit`). Sa propre adresse, `/feuille/:id` : le
 * retour du téléphone ramène à l'arbre.
 *
 * Le fil (chaque mère touchable, la branche en gras) ; l'intercalaire à la couleur claire de la
 * branche (ocre si elle attend le monde), son icône dossier, « faites/total » ; dessous, un paquet
 * de lignes : une case qui coche comme « C'est fait », le titre et sa ligne grise, sa réponse
 * attendue ou son échéance, › (toucher : sa fiche en page, 04 ; l'appui long : la fiche qui monte) ; une sous-branche y a l'icône dossier, sans case, et ouvre son
 * détail. « + un détail dans « X » » en dernière ligne. Sa note sur un post-it punaisé.
 *
 * L'intercalaire se touche : il ouvre la fiche de la branche (le titre du haut est celui du
 * projet). « Montrer ce qui est fait » du ⚙ agit ici aussi.
 */
export function DetailDUneBranche({
  donnees,
  montrerCeQuiEstFait,
  onMaillon,
  onEntrer,
  onFiche,
  onSaFiche,
  onRond,
}: {
  donnees: Feuille;
  montrerCeQuiEstFait: boolean;
  onMaillon: (id: string) => void;
  onEntrer: (id: string) => void;
  onFiche: (id: string) => void;
  /** La fiche de la branche elle-même. */
  onSaFiche: () => void;
  onRond: (bulle: Bulle) => void;
}) {
  const { bulle, chemin, enfants, ordreHierarchique } = donnees;
  const cadre = useCadreDuHaut();
  const lignes = montreesDansLArbre(rangerDansMonOrdre(enfants, ordreHierarchique), montrerCeQuiEstFait);
  const attend = attendLeMonde(bulle);
  // La couleur de la branche (la sienne, sinon celle de ses mères), sinon celle du projet (`--teinte`).
  const couleur = couleurDeBranche(bulle.id, (id) => (id === bulle.id ? bulle : undefined), bulle.couleurHeritee);
  const teinte = couleur ? teinteOuAccent(couleur) : 'var(--teinte)';
  const note = (bulle.notes ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <div className="detail-branche">
      <FilDuPouce chemin={chemin} onMaillon={onMaillon} />

      <button
        type="button"
        className={`detail-inter${attend ? ' est-en-attente' : ''}`}
        style={{ ['--teinte-branche' as string]: teinte }}
        onClick={onSaFiche}
      >
        <IconeDossier />
        {bulle.titre}
        <small>
          {faitesSurTotal(bulle.decompte.faites, bulle.decompte.total)}
          {attend && ATTEND_LE_MONDE_EN_MENTION}
        </small>
      </button>

      <div className="detail-paquet">
        {lignes.map((ligne) => (
          <LigneDuDetail key={ligne.id} bulle={ligne} onEntrer={onEntrer} onFiche={onFiche} onRond={onRond} />
        ))}
        <button type="button" className="detail-ligne detail-ligne--ajout" onClick={() => cadre?.ouvrirLePlusDans?.(bulle.id)}>
          {unDetailDans(bulle.titre)}
        </button>
      </div>

      {note.length > 0 && (
        <div className="detail-postit">
          <span className="detail-postit-punaise" aria-hidden="true" />
          <h2 className="detail-postit-titre">{NOTE_DE_LA_BRANCHE}</h2>
          {note.map((l, i) => (
            <p key={i} className="detail-postit-ligne">
              {l}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Le fil au téléphone (`.fil`), au-dessus du détail d'une branche et de la fiche en page :
 * chaque mère touchable, ramène à son niveau ; où l'on est, en gras.
 */
export function FilDuPouce({ chemin, onMaillon }: { chemin: readonly Maillon[]; onMaillon: (id: string) => void }) {
  const ici = chemin[chemin.length - 1];
  return (
    <nav className="detail-fil" aria-label="où tu es">
      {chemin.slice(0, -1).map((maillon) => (
        <span key={maillon.id}>
          <button type="button" className="detail-fil-maillon" data-tuto="fil" onClick={() => onMaillon(maillon.id)}>
            {maillon.titre}
          </button>
          {' › '}
        </span>
      ))}
      {ici && <b aria-current="page">{ici.titre}</b>}
    </nav>
  );
}

/** Une ligne du paquet : une tâche (la case, ce qu'elle dit, ›) ou une sous-branche (le dossier). */
function LigneDuDetail({
  bulle,
  onEntrer,
  onFiche,
  onRond,
}: {
  bulle: BulleDeFeuille;
  onEntrer: (id: string) => void;
  onFiche: (id: string) => void;
  onRond: (bulle: Bulle) => void;
}) {
  const sousBranche = bulle.nbEnfants > 0;
  const fait = bulle.etat === 'fait';
  const classe = `detail-ligne${fait ? ' est-fait' : ''}${attendLeMonde(bulle) ? ' est-en-attente' : ''}`;
  const grise = sousBranche ? null : ligneGriseDuDetail(bulle);
  const bout = sousBranche ? faitesSurTotal(bulle.decompte.faites, bulle.decompte.total) : ceQueDitLeBout(bulle);

  return (
    <div className={classe} data-tuto="bulle-liste" data-bulle={bulle.id}>
      {sousBranche ? (
        <IconeDossier />
      ) : (
        <button
          type="button"
          className={`detail-case${fait ? ' est-cochee' : ''}`}
          aria-label={titreDuRond(bulle.etat, true)}
          title={titreDuRond(bulle.etat, true)}
          onClick={() => onRond(bulle)}
        />
      )}
      <button
        type="button"
        className="detail-ouvrir"
        {...(sousBranche
          ? { onClick: () => onEntrer(bulle.id) }
          : gestesDuToucher(() => onEntrer(bulle.id), () => onFiche(bulle.id)))}
      >
        <span className="detail-t">
          {bulle.titre}
          {grise && <small>{grise}</small>}
        </span>
        {bout && <span className="detail-q">{bout}</span>}
        <span className="detail-chev" aria-hidden="true">
          ›
        </span>
      </button>
    </div>
  );
}
